import { useEffect, useState } from "react";

/**
 * Privacy Mode — device-local setting persisted to localStorage.
 *
 * When enabled, sensitive values (the member's hourly rate and monthly
 * pay) stay blurred and are never revealed on hover/focus.
 *
 * Storage key follows the app's namespaced preference convention
 * (see `ce_portal.notificationSoundPreference` in NotificationSettingsTab).
 *
 * Implementation: a tiny module-level store with a subscriber set, so
 * every mounted consumer updates live when the value changes from any
 * component (e.g. toggling in Profile Settings immediately re-blurs the
 * Timesheets totals without a reload).
 */
const STORAGE_KEY = "ce_portal.privacyMode";

const readStoredValue = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

let currentValue = readStoredValue();
const listeners = new Set();

const notifyListeners = () => {
  listeners.forEach((listener) => listener(currentValue));
};

/**
 * Persists the new value to localStorage and notifies all subscribers.
 * Safe to call from any component; no-op when the value is unchanged.
 */
export const setPrivacyMode = (enabled) => {
  const next = Boolean(enabled);
  if (next === currentValue) return;
  currentValue = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // Storage unavailable (private mode / quota) — in-memory value still applies.
  }
  notifyListeners();
};

/**
 * Returns `{ privacyMode, setPrivacyMode }`.
 * - `privacyMode`: current boolean state (defaults to false).
 * - `setPrivacyMode(bool)`: module-level setter — persists + notifies all
 *   subscribers, so every consumer re-renders live.
 */
export const usePrivacyMode = () => {
  const [privacyMode, setState] = useState(currentValue);

  useEffect(() => {
    const listener = (value) => setState(value);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  return { privacyMode, setPrivacyMode };
};

export default usePrivacyMode;